import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import { useReducer, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';

import {
  assignItemsToPickingArea,
  bulkUpdatePickingAreas,
  createPickingAreas,
  deletePickingAreas,
  unassignItemsFromArea,
  updatePickingAreas,
} from 'apis/picker-api/api';
import { PickingArea } from 'apis/picker-api/types';

import { UNASSIGNED_AREA_ID } from '../constants';
import { HeaderData, ItemData } from '../types';

const PICKING_AREA_PAYLOAD_ITEMS_LIMIT = 1000;
const PICKING_AREA_PAYLOAD_AREAS_LIMIT = 100;

export type Props = {
  venueId: string;
  activeAreaId: string | undefined;
  pickingAreas: PickingArea[] | undefined;
  unassignedAreaHeaders: HeaderData[] | undefined;
  isInSearchMode: boolean;
  refetchPickingAreas: () => Promise<any>;
  refetchActiveArea: () => Promise<any>;
  setActiveAreaId: (areaId: string) => void;
  onMoveItemsSuccess: () => void;
};

type ModalProps<T extends {} = {}, U = undefined> = {
  isOpen: boolean;
  isLoading: boolean;
  error: any;
  onConfirm: (...arg: U extends undefined ? [undefined?] : [U]) => void;
  onClose: () => void;
} & T;

export type Action =
  | { type: 'move-items-start'; itemsToAssign: ItemData[] }
  | { type: 'move-items-end' }
  | { type: 'create-from-cats-start' }
  | { type: 'create-from-cats-end' }
  | { type: 'create-area-start'; itemsToAssign: ItemData[] }
  | { type: 'create-area-end' }
  | { type: 'edit-area-start' }
  | { type: 'edit-area-end' }
  | { type: 'delete-area-start'; areaIdsToDelete: string[] }
  | { type: 'delete-area-end' };

type Option = { value: string; label: string; disabled?: boolean };

type State = {
  isMoveModalOpen: boolean;
  isCreateFromCatsOpen: boolean;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  areaIdsToDelete: string[];
  itemsToAssign: ItemData[];
};

const reducer = (state: State, action: Action): State => {
  const { type, ...payload } = action;
  switch (type) {
    case 'move-items-start':
      return { ...state, isMoveModalOpen: true, ...payload };
    case 'move-items-end':
      return { ...state, isMoveModalOpen: false, itemsToAssign: [] };
    case 'create-from-cats-start':
      return { ...state, isCreateFromCatsOpen: true };
    case 'create-from-cats-end':
      return { ...state, isCreateFromCatsOpen: false };
    case 'create-area-start':
      return { ...state, isCreateModalOpen: true, ...payload };
    case 'create-area-end':
      return { ...state, isCreateModalOpen: false, itemsToAssign: [] };
    case 'edit-area-start':
      return { ...state, isEditModalOpen: true };
    case 'edit-area-end':
      return { ...state, isEditModalOpen: false };
    case 'delete-area-start':
      return { ...state, isDeleteModalOpen: true, ...payload };
    case 'delete-area-end':
      return { ...state, isDeleteModalOpen: false, areaIdsToDelete: [] };
  }
};

const initialState: State = {
  isMoveModalOpen: false,
  isCreateFromCatsOpen: false,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  areaIdsToDelete: [],
  itemsToAssign: [],
};

const useModalState = ({
  venueId,
  activeAreaId,
  pickingAreas,
  unassignedAreaHeaders,
  isInSearchMode,
  refetchPickingAreas,
  refetchActiveArea,
  setActiveAreaId,
  onMoveItemsSuccess,
}: Props) => {
  const intl = useIntl();
  const [modalState, dispatchModalAction] = useReducer(reducer, initialState);

  const customAreas = useMemo(() => pickingAreas?.filter(area => area.id !== UNASSIGNED_AREA_ID), [pickingAreas]);

  const activeAreaName = useMemo(
    () => pickingAreas?.find(area => area.id === activeAreaId)?.name || '<area name not found>',
    [activeAreaId, pickingAreas],
  );

  const moveItemAreaOptions = useMemo(() => {
    if (!pickingAreas) {
      return [];
    }
    if (isInSearchMode) {
      return pickingAreas.map(area => ({
        value: area.id,
        label: area.name,
        disabled: modalState.itemsToAssign.some(item => item.areaId === area.id),
      }));
    }
    return pickingAreas.map(area => ({ label: area.name, value: area.id, disabled: area.id === activeAreaId }));
  }, [activeAreaId, isInSearchMode, modalState.itemsToAssign, pickingAreas]);

  const createFromCats = useMutation(
    async () => {
      if (activeAreaId !== UNASSIGNED_AREA_ID) {
        throw new Error(
          'Trying to create areas based on categories outside of the unassigned area. This should not happen',
        );
      }
      if (!unassignedAreaHeaders) {
        throw new Error(
          'unassignedAreaHeaders should be defined to create areas based on categories. This should not happen.',
        );
      }
      const bulkPickingAreasPayload = unassignedAreaHeaders
        .filter(header => header.data.id !== 'noCategory')
        .flatMap(header =>
          header.data.itemIds.map(itemId => ({ itemId, pickingAreaName: header.data.name, order: null })),
        );

      const pickingAreasPayloadChunks = splitEvery(
        new Set(bulkPickingAreasPayload.map(p => p.pickingAreaName)).size < PICKING_AREA_PAYLOAD_AREAS_LIMIT
          ? PICKING_AREA_PAYLOAD_ITEMS_LIMIT
          : PICKING_AREA_PAYLOAD_AREAS_LIMIT,
        bulkPickingAreasPayload,
      );

      for (const chunk of pickingAreasPayloadChunks) {
        await bulkUpdatePickingAreas(venueId, chunk);
      }

      await refetchPickingAreas();
      await refetchActiveArea();
      return unassignedAreaHeaders.length;
    },
    {
      onSuccess: count => {
        toast.success(intl.formatMessage({ id: 'picking-route.toast-area-created' }, { count }));
        dispatchModalAction({ type: 'create-from-cats-end' });
      },
    },
  );

  const createArea = useMutation(
    async (name: string) => {
      const result = await createPickingAreas(venueId, [{ name }]);
      if (modalState.itemsToAssign.length > 0) {
        await assignItemsToPickingArea(
          venueId,
          result.data[0].id,
          modalState.itemsToAssign.map(item => item.data.id),
        );
      }
      await refetchPickingAreas();
      return result;
    },
    {
      onSuccess: result => {
        dispatchModalAction({ type: 'create-area-end' });
        setActiveAreaId(result.data[0].id);
      },
    },
  );

  const updateArea = useMutation(
    async (name: string) => {
      if (!activeAreaId) {
        throw new Error('Active picking area not defined, this should not happen!');
      }
      await updatePickingAreas(venueId, [{ id: activeAreaId, name }]);
      await refetchPickingAreas();
    },
    {
      onSuccess: () => {
        dispatchModalAction({ type: 'edit-area-end' });
      },
    },
  );

  const deleteAreas = useMutation(
    async () => {
      await deletePickingAreas(venueId, modalState.areaIdsToDelete);
      await refetchPickingAreas();
      if (activeAreaId === UNASSIGNED_AREA_ID) {
        refetchActiveArea();
      }
    },
    {
      onSuccess: () => {
        dispatchModalAction({ type: 'delete-area-end' });
        setActiveAreaId(UNASSIGNED_AREA_ID);
      },
    },
  );

  const moveItems = useMutation(
    async ({ itemsToMove, toArea }: { itemsToMove: ItemData[]; toArea: Option }) => {
      let areaIdToMove: string = toArea.value;
      if (!pickingAreas?.find(pickingArea => pickingArea.id === toArea.value)) {
        const result = await createPickingAreas(venueId, [{ name: toArea.label }]);
        areaIdToMove = result.data[0].id;
      }

      if (areaIdToMove !== UNASSIGNED_AREA_ID) {
        await assignItemsToPickingArea(
          venueId,
          areaIdToMove,
          itemsToMove.map(item => item.data.id),
        );
      } else {
        const itemIdsIndexedByArea = itemsToMove.reduce<Record<string, string[]>>(
          (acc, curr) => ({
            ...acc,
            [curr.areaId]: acc[curr.areaId] ? [...acc[curr.areaId], curr.data.id] : [curr.data.id],
          }),
          {},
        );
        for (const [areaId, itemIds] of Object.entries(itemIdsIndexedByArea)) {
          await unassignItemsFromArea(venueId, areaId, itemIds);
        }
      }

      await refetchPickingAreas();
      await refetchActiveArea();
      return toArea.label;
    },
    {
      onSuccess: () => {
        onMoveItemsSuccess();
        dispatchModalAction({ type: 'move-items-end' });
      },
    },
  );

  const moveItemsModalProps = useMemo<ModalProps<{ options: Option[] }, Option>>(
    () => ({
      isOpen: modalState.isMoveModalOpen && !!activeAreaId,
      isLoading: moveItems.isLoading,
      error: moveItems.error,
      options: moveItemAreaOptions,
      onConfirm: toArea => moveItems.mutate({ itemsToMove: modalState.itemsToAssign, toArea }),
      onClose: () => dispatchModalAction({ type: 'move-items-end' }),
    }),
    [activeAreaId, modalState.isMoveModalOpen, modalState.itemsToAssign, moveItemAreaOptions, moveItems],
  );

  const createFromCatsModalProps = useMemo<ModalProps>(
    () => ({
      isOpen: modalState.isCreateFromCatsOpen,
      isLoading: createFromCats.isLoading,
      error: createFromCats.error,
      onConfirm: createFromCats.mutate,
      onClose: () => dispatchModalAction({ type: 'create-from-cats-end' }),
    }),
    [createFromCats.error, createFromCats.isLoading, createFromCats.mutate, modalState.isCreateFromCatsOpen],
  );

  const createAreaModalProps = useMemo<ModalProps<{}, string>>(
    () => ({
      isOpen: modalState.isCreateModalOpen,
      isLoading: createArea.isLoading,
      error: createArea.error,
      onConfirm: createArea.mutate,
      onClose: () => dispatchModalAction({ type: 'create-area-end' }),
    }),
    [createArea.error, createArea.isLoading, createArea.mutate, modalState.isCreateModalOpen],
  );

  const editAreaModalProps = useMemo<ModalProps<{ initialName: string }, string>>(
    () => ({
      isOpen: modalState.isEditModalOpen,
      isLoading: updateArea.isLoading,
      error: updateArea.error,
      initialName: activeAreaName,
      onConfirm: updateArea.mutate,
      onClose: () => dispatchModalAction({ type: 'edit-area-end' }),
    }),
    [activeAreaName, modalState.isEditModalOpen, updateArea.error, updateArea.isLoading, updateArea.mutate],
  );

  const deleteAreaModalProps = useMemo<ModalProps<{ mode: 'all-areas' | 'single-area' }>>(
    () => ({
      isOpen: modalState.isDeleteModalOpen,
      isLoading: deleteAreas.isLoading,
      error: deleteAreas.error,
      mode: modalState.areaIdsToDelete.length === customAreas?.length ? 'all-areas' : 'single-area',
      onConfirm: deleteAreas.mutate,
      onClose: () => dispatchModalAction({ type: 'delete-area-end' }),
    }),
    [
      customAreas?.length,
      deleteAreas.error,
      deleteAreas.isLoading,
      deleteAreas.mutate,
      modalState.areaIdsToDelete.length,
      modalState.isDeleteModalOpen,
    ],
  );

  return {
    moveItemsModalProps,
    createFromCatsModalProps,
    createAreaModalProps,
    editAreaModalProps,
    deleteAreaModalProps,
    dispatchModalAction,
  };
};

export default useModalState;
